import { createStore, useStore } from 'zustand';

import { authStore } from '@stores/auth';
import { fetchLookups } from '@lib/api';
import { firebase } from '@lib/firebase';
import { logger } from '@lib/logger';
import { updateAgentLastSeen } from '@lib/api/updateAgentLastSeen';

import { LookupsStateStore } from './types';
import { fromLookupsResponseToLookupsValue } from './utils/fromLookupsResponseToLookupsValue';

export const lookupsStore = createStore<LookupsStateStore>((set) => ({
  value: null,
  error: null,
  loading: false,
  fetch: async () => {
    try {
      set({ loading: true });
      const lookupsResponse = await fetchLookups();
      const lookupsValue = fromLookupsResponseToLookupsValue(lookupsResponse);
      set({ value: lookupsValue });

      firebase.setAnalyticsUserId(lookupsResponse.userUUID);

      // Set User Properties
      const userType = lookupsResponse.group
        ? 'Group'
        : lookupsResponse.restaurants.length > 0
        ? 'Restaurant'
        : 'Branches';

      firebase.setUserProperties({
        user_name: lookupsValue.userCode, // Custom key for $name
        distinct_id: lookupsValue.userUUID,
        user_type: userType,
      });
    } catch (error) {
      logger.error(error);
      set({ error });
      authStore.getState().logout();
    } finally {
      set({ loading: false });
    }
  },
}));

let isFetching = false;
let intervalId: ReturnType<typeof setTimeout>;
const time: number = firebase.getRemoteConfigNumber('LOG_LAST_SEEN');

authStore.subscribe(async (state) => {
  // on login, fetch lookups
  if (
    !lookupsStore.getState().value &&
    !isFetching &&
    state.computed.isLoggedIn
    // state.isLoggedInOnElmenus
  ) {
    isFetching = true;
    await lookupsStore.getState().fetch();
    isFetching = false;
    await updateAgentLastSeen();
    intervalId = setInterval(async () => {
      await updateAgentLastSeen();
    }, time * 60000);
  }

  // on logout: clear data
  if (lookupsStore.getState().value && !state.isLoggedInOnElmenus) {
    lookupsStore.setState({ value: null });

    clearInterval(intervalId);
  }
});

async function onLogin() {
  if (isFetching || lookupsStore.getState().value) return;

  isFetching = true;

  try {
    await lookupsStore.getState().fetch();
    await updateAgentLastSeen();

    intervalId = setInterval(
      updateAgentLastSeen,
      time * 60_000
    );
  } finally {
    isFetching = false;
  }
}

// initial state
async function initState() {
  if (authStore.getState().isLoggedInOnElmenus) {
    onLogin();
  }
}

initState()

export const useLookupsStore = () => useStore(lookupsStore);
